import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { Calendar as CalendarIcon, ArrowLeft, ArrowRight, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { ProviderCard, Provider } from "./ProviderCard";
import { TimeSlotSelector, TimeSlot } from "./TimeSlotSelector";
import { 
  generateTimeSlots, 
  generateAvailableDates
} from "@/services/mockBookingData";
import { TriageOutcome, TriageData } from "@/types/triage";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useProviders } from "@/hooks/useProvidersQuery";
import { useAuth } from "@/hooks/useAuth";

interface AudioTriageProviderSelectProps {
  triageResults?: TriageOutcome | null;
}

export function AudioTriageProviderSelect({ triageResults = null }: AudioTriageProviderSelectProps) {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Extract triage data from location state if available
  const locationState = location.state as {
    triageData?: TriageData;
    triageResult?: TriageOutcome;
    triageId?: string;
    patientId?: string;
    fromGuidedNextSteps?: boolean;
  } | null;
  
  // Use either passed props or location state data
  const effectiveTriageResults = triageResults || locationState?.triageResult || null;
  const triageData = locationState?.triageData || null;
  const triageId = locationState?.triageId;
  const patientId = locationState?.patientId;
  
  // Check if navigated from GuidedNextSteps
  const isFromGuidedNextSteps = Boolean(locationState?.fromGuidedNextSteps);
  
  // Get providers using useProviders hook
  const { profile } = useAuth();
  const { data: realProviders, isLoading } = useProviders(profile?.clinic_id || '');

  // State for transformed providers with matchScore
  const [providers, setProviders] = useState<Provider[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  
  // State for date selection
  const [date, setDate] = useState<Date | undefined>(new Date());
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  
  // State for time slot selection
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string | null>(null);

  // Transform real provider data to match ProviderCard interface
  useEffect(() => {
    if (realProviders && realProviders.length > 0) {
      const transformedProviders: Provider[] = realProviders.map((provider, index) => ({
        id: provider.id,
        name: provider.name,
        specialty: provider.specialty,
        experience: provider.experience,
        rating: provider.rating,
        matchScore: 95 - (index * 5), // Give high match scores with slight variation
        nextAvailable: provider.nextAvailable
      }));
      
      setProviders(transformedProviders);
      
      // Auto-select the first provider
      if (!selectedProvider) {
        setSelectedProvider(transformedProviders[0]);
      }
    }
    
    // Generate available dates
    setAvailableDates(generateAvailableDates());
  }, [realProviders, selectedProvider]);

  // Generate time slots when date or provider changes
  useEffect(() => {
    if (date) {
      setTimeSlots(generateTimeSlots(date));
      setSelectedTimeSlot(null); // Reset time slot selection when date changes
    }
  }, [date, selectedProvider]);

  // Handle provider selection
  const handleProviderSelect = (provider: Provider) => {
    setSelectedProvider(provider);
  };

  // Handle time slot selection
  const handleTimeSlotSelect = (slotId: string) => {
    setSelectedTimeSlot(slotId);
  };

  // Handle continue to confirmation
  const handleContinue = () => {
    if (!selectedProvider || !date || !selectedTimeSlot) {
      return; // Don't proceed if any selection is missing
    }

    const selectedSlot = timeSlots.find(slot => slot.id === selectedTimeSlot);
    
    if (!selectedSlot) return;

    // Navigate to confirmation screen with booking details
    navigate("/patient/audio-triage/book/confirm", {
      state: {
        provider: selectedProvider,
        date: format(date, "yyyy-MM-dd"),
        formattedDate: format(date, "EEEE, MMMM d, yyyy"),
        timeSlot: selectedSlot.time,
        duration: selectedSlot.duration,
        triageResults: effectiveTriageResults,
        triageData: triageData,
        triageId: triageId,
        patientId: patientId,
        primarySymptoms: effectiveTriageResults?.presentingComplaint || triageData?.symptoms?.join(", ")
      }
    });
  };

  // Handle back navigation
  const handleBack = () => {
    navigate(-1);
  };

  // Determine if continue button should be enabled
  const isContinueEnabled = selectedProvider && date && selectedTimeSlot;

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-8">
      {/* Header with back button */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={handleBack} className="flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <h1 className="text-2xl font-semibold text-center flex-1">Schedule Your Appointment</h1>
        <div className="w-20"></div> {/* Spacer for alignment */}
      </div>

      {/* Provider selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Select Provider
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-4 h-4 text-blue-500 cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">Providers are ranked based on their match to your symptoms and availability.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </CardTitle>
          <CardDescription>
            Based on your symptoms, we recommend these healthcare providers
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {providers?.map((provider) => (
              <ProviderCard
                key={provider.id}
                provider={provider}
                isSelected={selectedProvider?.id === provider.id}
                onSelect={handleProviderSelect}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Date and time selection */}
      {selectedProvider && (
        <Card>
          <CardHeader>
            <CardTitle>Select Date & Time</CardTitle>
            <CardDescription>
              Choose an available appointment slot with {selectedProvider.name}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Calendar for date selection */}
              <div>
                <h3 className="text-sm font-medium mb-2">Select Date</h3>
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={(date) => {
                    // Disable dates that are not in the available dates list
                    return (
                      !availableDates.includes(format(date, "yyyy-MM-dd")) ||
                      date < new Date()
                    );
                  }}
                  className="border rounded-md p-3"
                />
              </div>

              {/* Time slot selection */}
              <div>
                <h3 className="text-sm font-medium mb-2">Select Time</h3>
                <TimeSlotSelector
                  availableSlots={timeSlots}
                  selectedSlot={selectedTimeSlot}
                  onSelectSlot={handleTimeSlotSelect}
                  className="border rounded-md p-3"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Continue button */}
      <div className="flex justify-end">
        <Button
          onClick={handleContinue}
          disabled={!isContinueEnabled}
          className="flex items-center gap-2"
        >
          Continue to Confirmation
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
